<script setup>
/**
 * The crop every logo goes through (XN-4507, AC3-5).
 *
 * A logo never arrives at the right shape — it is usually a screenshot with
 * whitespace round it — so the frame is the slot's exact ratio and cannot be
 * resized. The IMAGE moves and scales under a fixed frame (the cropper's
 * "fixed stencil" mode), which is what "drag to reposition, zoom to scale"
 * means when the target is a fixed box.
 *
 * Output is always a PNG at 2x the slot, drawn from the cropper's canvas. That
 * one decision is what lets email use the same file: most mail clients will
 * not display an SVG, and rasterising at save time means there is never an
 * SVG to serve.
 */
import { computed, onMounted, ref } from 'vue';
import { Cropper, Preview } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import XlButton from '../xl/XlButton.vue';
import XlModal from '../xl/XlModal.vue';
import { SLOT_SHAPES } from './logoSlots';
import { useCropZoom, ZOOM_MAX, ZOOM_MIN } from '../../composables/useCropZoom';

const props = defineProps({
  src: { type: String, required: true },
  shape: { type: String, required: true, validator: (v) => v in SLOT_SHAPES },
  title: { type: String, default: 'Crop the logo' },
  // The last saved crop, in image pixels, so a filled slot resumes it.
  coordinates: { type: Object, default: null },
  // The parent's upload is still in flight after `save` has been emitted.
  saving: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'save']);

const target = computed(() => SLOT_SHAPES[props.shape]);

// The frame is drawn at 2x its slot so it is comfortable to work in, and the
// preview beside Save is at 1x — the size it will actually be seen at. On a
// phone 2x of the wide slot is 368px, wider than the modal body, and the
// body does not scroll sideways; so the scale drops until the frame fits
// the stage. The stage is MEASURED once mounted, since the modal's chrome
// is not a number worth guessing; until then (and under jsdom, which has
// no layout) the viewport minus a rough chrome stands in.
const FRAME_SCALE = 2;
const FRAME_SCALE_MIN = 0.5; // below 1x the frame is smaller than the slot; still usable, and it fits
const MODAL_CHROME = 140;
const fit = (width) => Math.min(FRAME_SCALE, Math.max(FRAME_SCALE_MIN, width / target.value.width));
const stageEl = ref(null);
const frameScale = ref(fit(window.innerWidth - MODAL_CHROME));
onMounted(() => {
  const width = stageEl.value?.clientWidth || 0;
  if (width) frameScale.value = fit(width);
});
const frame = computed(() => ({
  width: Math.round(target.value.width * frameScale.value),
  height: Math.round(target.value.height * frameScale.value),
}));

const cropperEl = ref(null);
const natural = ref({ width: 0, height: 0 });
const isSaving = ref(false);

// 100% is the image just covering the frame — the largest crop the frame can
// take, and the floor the cropper itself enforces, so "it can't go below the
// slot size" holds by construction. 300% is as tight as the ticket lets a
// crop go. How the zoom is read off the crop and set on it is shared with the
// photo crop on My details (composables/useCropZoom.js).
const { result, zoom, measure, onChange, setZoom } = useCropZoom(cropperEl);

// An image smaller than the frame cannot be zoomed to fit without upscaling.
// The cropper will not let the frame leave the image, so the floor holds; the
// warning is so the softness is not a surprise.
const isSoft = computed(() => natural.value.width > 0 && (
  natural.value.width < target.value.outWidth || natural.value.height < target.value.outHeight
));

function onReady() {
  // Measured BEFORE a saved crop is restored, or it would measure the restore.
  const first = measure();
  if (first?.image) natural.value = { width: first.image.width, height: first.image.height };

  // Resuming: the cropper moves and scales the image so the saved rectangle
  // lands under the fixed frame again, which is exactly "reopen the last crop".
  if (props.coordinates) cropperEl.value?.setCoordinates(props.coordinates);
}

// Nothing closes the crop while a save is under way — neither this
// component's own wait for the canvas nor the parent's uploads after it.
// A modal that vanished mid-save would look like a cancel that was not one.
const locked = computed(() => isSaving.value || props.saving);
function close() {
  if (!locked.value) emit('close');
}

async function save() {
  const out = cropperEl.value?.getResult();
  if (!out?.canvas) return;

  isSaving.value = true;
  try {
    const blob = await new Promise((resolve, reject) => {
      out.canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('The crop produced no image'))), 'image/png');
    });
    emit('save', { blob, coordinates: out.coordinates, natural: { ...natural.value } });
  } catch (error) {
    window.loadNotification?.({ message: error.message, error: true });
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <XlModal :title="props.title" max-width="720px" bordered :close-on-backdrop="false" @close="close">
    <div class="logo-crop">
      <div ref="stageEl" class="logo-crop__stage">
        <!-- No debounce and no transitions (XN-4578): the slider zooms by a
             factor relative to the last change, so a change that arrives
             500ms late, or a zoom the cropper drops while it is still
             animating the previous one, compounds into a jump on release. -->
        <Cropper
          ref="cropperEl"
          class="logo-crop__cropper"
          background-class="logo-crop__bg"
          :src="props.src"
          :stencil-size="frame"
          :stencil-props="{ aspectRatio: target.width / target.height, handlers: {}, movable: false, resizable: false }"
          image-restriction="stencil"
          :resize-image="{ wheel: false, touch: false, adjustStencil: false }"
          :debounce="false"
          :transitions="false"
          :canvas="{ width: target.outWidth, height: target.outHeight }"
          @ready="onReady"
          @change="onChange"
        />

        <label class="logo-crop__zoom">
          <span>Zoom</span>
          <input
            type="range"
            :min="ZOOM_MIN"
            :max="ZOOM_MAX"
            step="0.05"
            :value="zoom"
            aria-label="Zoom"
            @input="setZoom($event.target.value)"
          />
          <b>{{ Math.round(zoom * 100) }}%</b>
        </label>

        <p class="logo-crop__hint">Drag the image to reposition it, and use the slider to zoom. The frame is the exact shape of the slot and cannot be resized.</p>
        <p v-if="isSoft" class="logo-crop__warn">
          This image is smaller than the {{ target.label }} slot ({{ natural.width }} × {{ natural.height }}, against {{ target.outWidth }} × {{ target.outHeight }} needed), so it will look soft.
        </p>
      </div>

      <div class="logo-crop__side">
        <b>At final size</b>
        <div class="logo-crop__preview" :style="{ width: `${target.width}px`, height: `${target.height}px` }">
          <Preview
            v-if="result.image"
            :width="target.width"
            :height="target.height"
            :image="result.image"
            :coordinates="result.coordinates"
          />
        </div>
        <span>{{ target.width }} × {{ target.height }}, as it will be seen</span>
      </div>
    </div>

    <template #actions>
      <XlButton variant="ghost" :disabled="locked" @click="close">Cancel</XlButton>
      <XlButton :loading="isSaving || props.saving" data-testid="logo-crop-save" @click="save">Save</XlButton>
    </template>
  </XlModal>
</template>

<style scoped>
.logo-crop, .logo-crop * { box-sizing: border-box; }

.logo-crop {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.logo-crop__stage {
  flex: 1 1 300px;
  min-width: 0;
}

.logo-crop__cropper {
  height: 320px;
  max-width: 100%;
  border-radius: var(--xl-radius-md);
  overflow: hidden;
}

/* A checkerboard behind the image is what shows a transparent PNG's
   transparency before it is saved, rather than after it lands on a page.
   :deep because the class lands on an element inside the cropper, which does
   not carry this component's scope attribute — without it the rule matched
   nothing and the board was plain white. */
.logo-crop :deep(.logo-crop__bg) {
  background-color: #fff;
  background-image:
    linear-gradient(45deg, #e6e2ea 25%, transparent 25%),
    linear-gradient(-45deg, #e6e2ea 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e6e2ea 75%),
    linear-gradient(-45deg, transparent 75%, #e6e2ea 75%);
  background-size: 16px 16px;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0;
}

.logo-crop__zoom {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  font-size: var(--xl-type-body-small-size);
  color: var(--xl-color-secondary-text);
}

.logo-crop__zoom input { flex: 1; }
.logo-crop__zoom b { min-width: 42px; text-align: right; font-variant-numeric: tabular-nums; }

.logo-crop__hint {
  margin: 10px 0 0;
  font-size: var(--xl-type-body-small-size);
  color: var(--xl-color-outline);
}

.logo-crop__warn {
  margin: 8px 0 0;
  padding: 10px 12px;
  border-radius: var(--xl-radius-sm);
  background: color-mix(in srgb, var(--xl-color-warning) 12%, white);
  font-size: var(--xl-type-body-small-size);
  color: var(--xl-color-warning);
}

.logo-crop__side {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  font-size: var(--xl-type-body-small-size);
  color: var(--xl-color-secondary-text);
}

.logo-crop__side b { color: var(--xl-color-primary-text); }

.logo-crop__preview {
  border: 1px dashed var(--xl-color-border);
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}
</style>
